import * as React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button, FormGroup, InputGroup, Icon } from "@blueprintjs/core";
import { Spacer } from "components/common";
import { useControlledInput } from "../../hooks";
import { useAuthActions } from "context/";
import { withPreventDefault, validatePassword } from "utils";
import Error from "./Error";
import PasswordRequirements from "./PasswordRequirements";
import { css } from "@emotion/core";

const notEmpty = (v) => v !== "";

const ResetPassword = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { requestCode, passwordReset } = useAuthActions();

  const [hasCode, setHasCode] = React.useState(
    new URLSearchParams(location.search).get("code") || false
  );
  const [checkEmail, setCheckEmail] = React.useState(false);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const {
    valid: $email,
    reset: resetEmail,
    ...email
  } = useControlledInput(notEmpty);
  const {
    valid: $code,
    reset: resetCode,
    ...code
  } = useControlledInput(notEmpty);

  const {
    valid: $password,
    reset: resetPassword,
    ...password
  } = useControlledInput(validatePassword);
  const {
    valid: $confirmation,
    reset: resetConfirmation,
    ...confirmation
  } = useControlledInput((v) => $password && v === password.value);

  if (props.currentUser) {
    return <Navigate to="/folders" />;
  }

  const valid = $email && $code && $password && $confirmation;

  React.useEffect(() => {
    const query = new URLSearchParams(location.search);
    const searchEmail = query.get("email");
    const searchCode = query.get("code");

    if (searchEmail) {
      email.onChange({ target: { value: searchEmail } });
    }

    if (searchCode) {
      code.onChange({ target: { value: searchCode } });
    }
  }, [location.search]);

  if (checkEmail) {
    return (
      <div>
        <p>Check your email for a reset code.</p> <Spacer />
        <Button
          intent="primary"
          fill
          type="button"
          onClick={() => {
            setCheckEmail(false);
            setHasCode(true);
          }}
        >
          Enter Reset Code
        </Button>
      </div>
    );
  }

  if (hasCode === false) {
    return (
      <form
        onSubmit={withPreventDefault((event) => {
          setLoading(true);
          requestCode(email.value).then(() => {
            setLoading(false);
            setCheckEmail(true);
          });
        })}
      >
        <FormGroup label="Login Email" labelFor="email-input">
          <InputGroup id="email-input" placeholder="Login Email" {...email} />
        </FormGroup>
        <Button
          intent="primary"
          fill
          disabled={$email === false}
          type="submit"
          loading={loading}
        >
          Send Reset Code
        </Button>
        <Spacer />
        <a onClick={() => setHasCode(true)}>I already have a code.</a>
      </form>
    );
  }

  return (
    <form
      onSubmit={withPreventDefault((event) => {
        setLoading(true);
        passwordReset(email.value, code.value, password.value).then(
          (result) => {
            if (result !== true) {
              setLoading(false);
              setError(result);
            }
          }
        );
      })}
    >
      <FormGroup label="Login Email" labelFor="email-input">
        <InputGroup id="email-input" placeholder="Login Email" {...email} />
      </FormGroup>
      <FormGroup
        label="Reset Code"
        labelFor="code-input"
        helperText="Contact support@fybr.ca to obtain a reset code."
      >
        <InputGroup id="code-input" placeholder="Reset Code" {...code} />
      </FormGroup>
      <FormGroup label="Password" labelFor="password-input">
        <InputGroup
          id="password-input"
          type="password"
          placeholder="Password"
          {...password}
        />
        <PasswordRequirements password={password.value} />
      </FormGroup>
      <FormGroup label="Password Confirmation" labelFor="confirmation-input">
        <InputGroup
          id="confirmation-input"
          type="password"
          placeholder="Password Confirmation"
          {...confirmation}
          rightElement={
            $confirmation ? (
              <div
                css={css`
                  height: 30px;
                  width: 32px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
              >
                <Icon icon="tick" intent="success" />
              </div>
            ) : undefined
          }
        />
      </FormGroup>
      <Error message={error} />
      <Button
        intent="primary"
        fill
        disabled={valid == false}
        type="submit"
        loading={loading}
      >
        Reset Password
      </Button>
      {error && (
        <>
          <Spacer />
          <Button
            intent="primary"
            fill
            type="button"
            onClick={() => navigate("/login")}
          >
            Login
          </Button>
        </>
      )}
    </form>
  );
};

export default ResetPassword;
